import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { makeStyles } from '@mui/styles';
import {
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   Paper,
   CircularProgress,
   Box,
   Typography,
   Button,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
} from '@mui/material';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';

const ALL_USERS = 'all';

const useStyles = makeStyles(theme => ({
   container: {
      marginTop: theme.spacing(2),
   },
   table: {
      minWidth: 750,
   },
   tableHeader: {
      backgroundColor: theme.palette.grey[200],
      fontWeight: 'bold',
   },
   userRow: {
      backgroundColor: theme.palette.grey[100],
   },
   noData: {
      textAlign: 'center',
      padding: theme.spacing(3),
   },
   monospace: {
      fontFamily: 'monospace',
      fontSize: '0.8rem',
   },
}));

const GAUGE_DEVICES_QUERY = gql`
   query getGaugeDevices($operatorId: Int!) {
      gaugeDevices: gaugeDevices_ByOperatorId(operatorId: $operatorId) {
         id
         userId
         serialNumber
         manufacturer
         deviceName
         bleDeviceId
         platform
         macAddress
         softwareVersion
         lastConnectedDateTime
         user {
            id
            name
            email
         }
      }
   }
`;

/**
 * Format a Sequelize DATE (Unix milliseconds as a string, or ISO 8601) as YYYY-MM-DD HH:mm
 */
const formatDateTime = (dateInput) => {
   if (!dateInput) return '-';
   const timestamp = Number(dateInput);
   const date = isNaN(timestamp) ? new Date(dateInput) : new Date(timestamp);
   if (isNaN(date.getTime())) return '-';

   const pad = (value) => String(value).padStart(2, '0');
   return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getUserLabel = (device) => device.user?.name || device.user?.email || `User ${device.userId}`;

/**
 * Gauges each user has selected in the mobile app, grouped by user.
 */
export default forwardRef(function GaugeDevicesTable({ operatorId }, ref) {
   const classes = useStyles();
   const [selectedUserId, setSelectedUserId] = useState(ALL_USERS);
   const { data, loading, error, refetch } = useQuery(GAUGE_DEVICES_QUERY, {
      variables: { operatorId },
      skip: !operatorId,
      fetchPolicy: 'cache-and-network',
   });

   // Expose refetch function via ref
   useImperativeHandle(ref, () => ({
      refetch: () => refetch(),
   }));

   // [{ userId, label, devices: [...] }] sorted by user name
   const groups = useMemo(() => {
      const byUser = {};
      (data?.gaugeDevices || []).forEach((device) => {
         if (!byUser[device.userId]) {
            byUser[device.userId] = { userId: device.userId, label: getUserLabel(device), email: device.user?.email, devices: [] };
         }
         byUser[device.userId].devices.push(device);
      });
      return Object.values(byUser).sort((a, b) => a.label.localeCompare(b.label));
   }, [data]);

   const visibleGroups = selectedUserId === ALL_USERS
      ? groups
      : groups.filter((group) => String(group.userId) === String(selectedUserId));

   if (error) {
      return <DataLoadingAndErrors error={error} isLoading={false} />;
   }

   if (loading && !data) {
      return (
         <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
         </Box>
      );
   }

   if (groups.length === 0) {
      return (
         <Box className={classes.noData}>
            <Typography variant="body1" color="textSecondary">
               No devices recorded
            </Typography>
         </Box>
      );
   }

   return (
      <Box>
         <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
               <InputLabel id="gauge-devices-user-label">User</InputLabel>
               <Select
                  labelId="gauge-devices-user-label"
                  label="User"
                  value={selectedUserId}
                  onChange={(event) => setSelectedUserId(event.target.value)}
               >
                  <MenuItem value={ALL_USERS}>All users</MenuItem>
                  {groups.map((group) => (
                     <MenuItem key={group.userId} value={group.userId}>
                        {group.label} ({group.devices.length})
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <Button
               variant="contained"
               color="primary"
               size="small"
               onClick={() => refetch()}
            >
               Refresh
            </Button>
         </Box>
         <TableContainer component={Paper} className={classes.container}>
            <Table className={classes.table} size="small">
               <TableHead>
                  <TableRow className={classes.tableHeader}>
                     <TableCell><strong>Serial Number</strong></TableCell>
                     <TableCell><strong>Manufacturer</strong></TableCell>
                     <TableCell><strong>Device Name</strong></TableCell>
                     <TableCell align="center"><strong>Platform</strong></TableCell>
                     <TableCell><strong>BLE Device Id</strong></TableCell>
                     <TableCell align="center"><strong>Software Version</strong></TableCell>
                     <TableCell align="center"><strong>Last Connected</strong></TableCell>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {visibleGroups.map((group) => (
                     <React.Fragment key={group.userId}>
                        <TableRow className={classes.userRow}>
                           <TableCell colSpan={7}>
                              <strong>{group.label}</strong>
                              {group.email && group.email !== group.label && ` (${group.email})`}
                              {` — ${group.devices.length} device${group.devices.length === 1 ? '' : 's'}`}
                           </TableCell>
                        </TableRow>
                        {group.devices.map((device) => (
                           <TableRow key={device.id} hover>
                              <TableCell>{device.serialNumber}</TableCell>
                              <TableCell>{device.manufacturer}</TableCell>
                              <TableCell>{device.deviceName || '-'}</TableCell>
                              <TableCell align="center">{device.platform || '-'}</TableCell>
                              <TableCell className={classes.monospace}>
                                 {device.bleDeviceId || '-'}
                                 {device.macAddress && <div>{device.macAddress}</div>}
                              </TableCell>
                              <TableCell align="center">{device.softwareVersion || '-'}</TableCell>
                              <TableCell align="center">{formatDateTime(device.lastConnectedDateTime)}</TableCell>
                           </TableRow>
                        ))}
                     </React.Fragment>
                  ))}
               </TableBody>
            </Table>
         </TableContainer>
      </Box>
   );
});
